import { Injectable } from '@angular/core';
import { Produit } from '../../models/produit.model';
import { HttpClient } from '@angular/common/http';
import { JsonMapperService } from 'src/app/core/services/mapper/mapper.service';
import { Observable, MonoTypeOperatorFunction, OperatorFunction, forkJoin, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { ClearCache, Cache, CacheKey } from 'src/app/core/services/cache/cache.service';
import { Context, SessionContext } from 'src/app/core/services/config/app.settings';
import { UtilisateurTypeCode } from 'src/app/data/intervenant/models/enums/type-utilisateur.enum';

interface ObjectWithProduitsList {
    codesProduit: string[];
    produits: Produit[];
}
interface ObjectWithProduit {
    codeProduit: string;
    produit: Produit;
}

@Injectable({
    providedIn: 'root'
})
export class ProduitsService {
    constructor(private readonly http: HttpClient, private readonly mapper: JsonMapperService) {}

    @ClearCache
    clearCache() {}

    @Cache()
    getProduitByCode(@CacheKey codeProduit: string): Observable<Produit> {
        return this.http
            .get<object>(`/api/habilitation/private/produits/${btoa(codeProduit)}`)
            .pipe(map(produit => this.mapper.deserializeObject(produit, Produit)));
    }

    @Cache()
    getProduitsByCdcs(@CacheKey idOrganisme: number, @CacheKey idsCdcs: number[]): Observable<Produit[]> {
        if (SessionContext.get('utilisateurType') === UtilisateurTypeCode.OPERATEUR) {
            return this.http
                .get<object[]>(`/api/habilitation/private/intervenants/${SessionContext.get('idIntervenant')}/produits?cdc=${idsCdcs.join(',')}`)
                .pipe(map(produits => this.mapper.deserializeArray(produits, Produit)));
        } else {
            return this.http
                .get<object[]>(`/api/habilitation/private/organismes/${idOrganisme}/produits?cdc=${idsCdcs.join(',')}`)
                .pipe(map(produits => this.mapper.deserializeArray(produits, Produit)));
        }
    }

    getProduitPipe<T extends ObjectWithProduit | ObjectWithProduit[]>(): OperatorFunction<T, T> {
        return switchMap((list: T) => {
            const liste: ObjectWithProduit[] = [].concat(list);

            const produitRequests = liste.map(item =>
                this.getProduitByCode(item.codeProduit).pipe(
                    tap(produit => (item.produit = produit)),
                    take(1)
                )
            );

            return forkJoin(produitRequests.length ? produitRequests : [of(null)]).pipe(map(() => list));
        });
    }

    getProduitsPipe<T extends ObjectWithProduitsList | ObjectWithProduitsList[]>(): OperatorFunction<T, T> {
        return tap((list: T) => {
            const liste: ObjectWithProduitsList[] = [].concat(list);

            liste.forEach(item => {
                item.codesProduit.forEach(codeProduit => {
                    this.getProduitByCode(codeProduit).subscribe(produit => item.produits.push(produit));
                });
            });
        });
    }
}
